import {Interval} from "luxon";
import {inject, singleton} from "tsyringe";
import {Issue} from "../domain/entities/issue.entity";
import {IssueRepository} from "../domain/repositories/issue.repository";
import {IntervalLoadingGuard, ReservationToken} from "./interval-loading.guard";

/**
 * Interface for request operations
 */
export interface TimelineRequest {
    searchInterval: Interval;
    requestId: string;
    timestamp: number;
}

/**
 * Result of a timeline request operation
 */
export interface TimelineRequestResult {
    issuesByDay: Map<number, Map<string, Issue>>;
    loadedIntervals: Interval[];
}

/**
 * Service responsible for managing timeline requests with debouncing,
 * cancellation and race condition prevention.
 */
@singleton()
export class TimelineRequestManager {

    private _pendingRequests = new Map<string, AbortController>();
    private _latestRequest: { interval: Interval; promise: Promise<TimelineRequestResult> } | null = null;
    
    constructor(
        @inject(IntervalLoadingGuard) private readonly _intervalGuard: IntervalLoadingGuard,
        @inject(IssueRepository) private readonly _issueRepository: IssueRepository
    ) {
        this._setupCleanupInterval();
    }

    /**
     * Request issues for an interval with debouncing and race condition prevention
     * 
     * @param searchInterval Interval to load issues for
     * @returns Promise with loaded issues
     */
    public async requestIssuesForInterval(searchInterval: Interval): Promise<TimelineRequestResult> {
        // For immediate requests (non-drag scenarios), execute directly
        // Debouncing is handled by the internal pipeline for rapid successive calls
        
        // Generate unique request ID
        const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Create request
        const request: TimelineRequest = {
            searchInterval,
            requestId,
            timestamp: Date.now()
        };

        // Execute request directly
        return this._executeRequest(request);
    }

    /**
     * Request issues with debouncing - specifically for drag operations
     * 
     * KISS Solution: Cache-based deduplication eliminates Promise-Observable mismatch
     * Same interval = same promise (deduplication), different interval = new request
     * 
     * @param searchInterval Interval to load issues for
     * @returns Promise with loaded issues
     */
    public requestIssuesForIntervalDebounced(searchInterval: Interval): Promise<TimelineRequestResult> {
        // Check if we already have a request for the same interval
        if (this._latestRequest && this._latestRequest.interval.equals(searchInterval)) {
            return this._latestRequest.promise;
        }

        // Cancel any pending requests (new interval = old requests obsolete)
        this.cancelAllRequests();

        // Create new request promise - direct execution, no complex Observable chain
        const requestPromise = this._createDebouncedRequest(searchInterval);

        // Cache the latest request for deduplication
        this._latestRequest = { interval: searchInterval, promise: requestPromise };
        
        return requestPromise;
    }

    /**
     * Create a debounced request using simple timeout-based debouncing
     */
    private async _createDebouncedRequest(searchInterval: Interval): Promise<TimelineRequestResult> {
        // Simple debouncing: wait 150ms, then check if we're still the latest request
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Check if we're still the latest request (another request might have superseded us)
        if (this._latestRequest?.interval.equals(searchInterval)) {
            // Generate request ID and execute
            const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const request: TimelineRequest = {
                searchInterval,
                requestId,
                timestamp: Date.now()
            };
            
            return this._executeRequest(request);
        } else {
            // We've been superseded, return empty result
            return {
                issuesByDay: new Map(),
                loadedIntervals: []
            };
        }
    }

    /**
     * Cancel all pending requests (e.g., when view is destroyed)
     */
    public cancelAllRequests(): void {
        for (const [requestId, controller] of this._pendingRequests.entries()) {
            controller.abort();
            this._pendingRequests.delete(requestId);
        }
        
        // Clear latest request cache to prevent stale promises
        this._latestRequest = null;
    }

    /**
     * Get number of currently pending requests (for debugging)
     */
    public getPendingRequestCount(): number {
        return this._pendingRequests.size;
    }


    /**
     * Execute a single request with reservation and cancellation support
     */
    private async _executeRequest(request: TimelineRequest): Promise<TimelineRequestResult> {
        const { searchInterval, requestId } = request;

        // Step 1: Reserve intervals atomically
        const reservation = this._intervalGuard.reserveAndFindGaps(searchInterval);
        
        // If no gaps, return empty result
        if (reservation.intervals.length === 0) {
            this._intervalGuard.cancelReservation(reservation);
            return {
                issuesByDay: new Map(),
                loadedIntervals: []
            };
        }

        // Step 2: Setup cancellation
        const abortController = new AbortController();
        this._pendingRequests.set(requestId, abortController);

        try {
            // Step 3: Execute requests for all gap intervals
            const requests: Promise<Issue[]>[] = [];
            
            for (const interval of reservation.intervals) {
                const requestPromise = this._issueRepository.findAllByInterval(interval)
                    .then(issues => {
                        // Check if request was cancelled - return empty result instead of throwing
                        if (abortController.signal.aborted) {
                            return [];
                        }
                        return issues;
                    });
                
                requests.push(requestPromise);
            }

            // Step 4: Wait for all requests to complete
            const allIssuesArrays = await Promise.all(requests);
            
            // Check again if cancelled during request - return empty result gracefully
            if (abortController.signal.aborted) {
                this._intervalGuard.cancelReservation(reservation);
                return {
                    issuesByDay: new Map(),
                    loadedIntervals: []
                };
            }

            // Step 5: Process results and build response
            const issuesByDay = new Map<number, Map<string, Issue>>();
            const allIssues = allIssuesArrays.flat();

            // Clear existing data for loaded intervals
            for (const interval of reservation.intervals) {
                interval.splitBy({day: 1}).forEach(dayInterval => {
                    const unixDateTime = dayInterval.start!.toUnixInteger();
                    issuesByDay.delete(unixDateTime);
                });
            }

            // Add issues to day map
            for (const issue of allIssues) {
                for (let i = 0; i < issue.interval.count('days'); i++) {
                    const unixDateTime = issue.start.plus({day: i}).toUnixInteger();
                    if (!issuesByDay.has(unixDateTime)) {
                        issuesByDay.set(unixDateTime, new Map<string, Issue>());
                    }

                    const issuesForDay = issuesByDay.get(unixDateTime)!;
                    if (!issuesForDay.has(issue.uid)) {
                        issuesForDay.set(issue.uid, issue);
                    }
                }
            }

            // Step 6: Complete reservation
            this._intervalGuard.completeReservation(reservation);

            return {
                issuesByDay,
                loadedIntervals: reservation.intervals
            };

        } catch (error) {
            // Step 7: Handle errors - cancel reservation
            this._intervalGuard.cancelReservation(reservation);
            throw error;
        } finally {
            // Step 8: Cleanup
            this._pendingRequests.delete(requestId);
        }
    }

    /**
     * Setup interval for cleaning up old reservations and requests
     */
    private _setupCleanupInterval(): void {
        setInterval(() => {
            this._intervalGuard.cleanupOldReservations();
            
            // Cleanup old pending requests (older than 30 seconds)
            const now = Date.now();
            const maxAge = 30 * 1000; // 30 seconds
            
            for (const [requestId, controller] of this._pendingRequests.entries()) {
                // Extract timestamp from request ID
                const timestamp = parseInt(requestId.split('-')[0]);
                if (now - timestamp > maxAge) {
                    controller.abort();
                    this._pendingRequests.delete(requestId);
                }
            }
        }, 60000); // Run every minute
    }
}